import { useEffect, useState } from "react";
import { Numpad } from "../components/StreetPay/Numpad";
import StripePaymentForm from "../components/StreetPay/StripePaymentForm";
import AnimatedBackground from "../components/AnimatedBackground";

const API_URL = process.env.REACT_APP_API_URL;

// Success screen component - Cyber theme
function SuccessScreen({ amount }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center relative overflow-hidden bg-[#020403]"
      style={{ paddingTop: '73px' }}>

      {/* Animated Background - Coding/Tech Theme */}
      <AnimatedBackground
        imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
        opacity={0.5}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-[#0a110e] border border-green-500/30 shadow-2xl overflow-hidden p-10 text-center holo-card"
          style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 30px), calc(100% - 30px) 100%, 0 100%)'}}>

          {/* Success Icon */}
          <div className="mb-6 flex h-24 w-24 mx-auto items-center justify-center rounded-full bg-green-500/20 border-2 border-green-500">
            <svg
              className="h-12 w-12 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="mb-3 font-bold text-3xl text-white tracking-widest uppercase" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Payment Sent!
          </h1>
          <div className="mb-2 text-5xl font-bold text-green-500 tracking-tight font-mono">
            ${amount}
          </div>
          <p className="mb-8 text-gray-400 text-sm font-mono">
            &gt; Transaction processed successfully
          </p>

          <a
            href="/StreetPay"
            className="block w-full bg-green-500 hover:bg-white text-black px-8 py-4 font-bold uppercase tracking-widest text-lg transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:shadow-[0_0_25px_#00ff41]"
          >
            Done
          </a>
        </div>
      </div>
    </div>
  );
}

export default function StreetPayPage() {
  const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
  const [amount, setAmount] = useState("0");
  const [clientSecret, setClientSecret] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successAmount, setSuccessAmount] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for success redirect from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const returnedAmount = urlParams.get("amount");

    if (success === "true" && returnedAmount) {
      setPaymentSuccess(true);
      setSuccessAmount(returnedAmount);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const formatAmount = (value) => {
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleKeyPress = (key) => {
    if (key === "." && amount.includes(".")) return;
    // Limit decimal places to 2
    if (amount.includes(".")) {
      const decimalPart = amount.split(".")[1];
      if (decimalPart && decimalPart.length >= 2) return;
    }
    if (amount === "0" && key !== ".") {
      setAmount(key);
    } else {
      setAmount(amount + key);
    }
  };

  const handleDelete = () => {
    setAmount((prev) => (prev.length > 1 ? prev.slice(0, -1) : "0"));
  };

  const handlePayClick = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/StreetPay/payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount }),
      });

      const data = await response.json();

      if (data.ok && data.clientSecret) {
        setClientSecret(data.clientSecret);
        setShowPaymentForm(true);
        setError(null);
      } else if (data.errorMessage) {
        setError(data.errorMessage);
      }
    } catch (err) {
      setError("Failed to create payment intent");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  // Show success screen
  if (paymentSuccess) {
    return <SuccessScreen amount={successAmount} />;
  }

  // Show payment form with Stripe Elements - Cyber theme
  if (showPaymentForm && clientSecret && stripePublishableKey) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center overflow-hidden relative bg-[#020403]"
        style={{ paddingTop: '73px', padding: '16px' }}>

        {/* Animated Background - Coding/Tech Theme */}
        <AnimatedBackground
          imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
          opacity={0.5}
        />

        <div className="relative z-10 w-full max-w-md px-6">
          {/* Amount Display */}
          <div className="text-center mb-8">
            <div className="text-gray-400 text-sm font-mono tracking-widest uppercase mb-2">[ PAYMENT AMOUNT ]</div>
            <div className="text-green-500 text-6xl font-bold tracking-tight font-mono">
              ${formatAmount(amount)}
            </div>
          </div>

          {/* Payment Form Card */}
          <div className="bg-[#0a110e] border border-blue-400/20 shadow-2xl overflow-hidden holo-card"
            style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 30px), calc(100% - 30px) 100%, 0 100%)'}}>
            <StripePaymentForm
              clientSecret={clientSecret}
              stripePublishableKey={stripePublishableKey}
              amount={amount}
              onCancel={handleCancel}
            />
          </div>
        </div>
      </div>
    );
  }

  // Show numpad for amount entry - Cyber theme
  return (
    <div className="flex min-h-screen flex-col items-center justify-center overflow-hidden relative bg-[#020403]"
      style={{ paddingTop: '73px' }}>

      {/* Animated Background - Coding/Tech Theme */}
      <AnimatedBackground
        imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
        opacity={0.5}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <span className="material-symbols-outlined text-green-500 text-5xl animate-pulse">payments</span>
          </div>
          <h1 className="text-white text-3xl font-bold tracking-widest uppercase" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            StreetPay
          </h1>
          <p className="text-gray-400 text-sm mt-1 font-mono">&gt; Fast. Secure. Simple.</p>
        </div>

        {/* Main Card - Cyber theme */}
        <div className="bg-[#0a110e] border border-blue-400/20 shadow-2xl overflow-hidden mb-6 holo-card"
          style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 30px), calc(100% - 30px) 100%, 0 100%)'}}>

          {/* Amount Display */}
          <div className="px-8 pt-10 pb-6 text-center">
            <div className="text-gray-400 text-sm font-mono tracking-widest uppercase mb-2">[ AMOUNT ]</div>
            <div className="text-6xl font-bold text-green-500 tracking-tight mb-1 font-mono">
              ${formatAmount(amount)}
            </div>
            <div className="text-gray-500 text-xs font-mono">&gt; Enter amount to send</div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mb-4 px-4 py-3 bg-red-500/10 border-l-4 border-red-500 rounded-r">
              <p className="text-red-400 text-sm font-mono">{error}</p>
            </div>
          )}

          {/* Numpad */}
          <div className="px-6 pb-6">
            <Numpad
              onKeyPress={handleKeyPress}
              onDelete={handleDelete}
              onSubmit={handlePayClick}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 text-gray-400 text-xs font-mono">
          <span className="material-symbols-outlined text-sm text-green-500">lock</span>
          <span>&gt; Secured with Stripe</span>
        </div>
      </div>
    </div>
  );
}
